import React from "react";
import { Sparkles, Info } from "lucide-react";
import { DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { elideFolderPath } from "../../types/organize";

interface OrganizeTargetHeaderProps {
  projectName: string;
  /** Ancestors of the target folder, target last. Empty for a project-root run. */
  folderPath: string[];
}

/**
 * Says exactly what is about to be reorganised: the project, then the path down
 * to the target folder. A building can hold several projects with similarly
 * named folders, so "Organise folder Joonised" alone is ambiguous.
 *
 * The middle of a long path is elided rather than truncated at the end: the
 * project and the folder actually being organised are the two segments the
 * reader needs, and they are the two an end-truncation would lose.
 */
export function OrganizeTargetHeader({ projectName, folderPath }: OrganizeTargetHeaderProps) {
  const { t } = useLanguage();
  const { head, elided, tail } = elideFolderPath(folderPath);
  const fullPath = folderPath.join(" › ");
  // One flat list, so the separator is simply "between items" and a short path
  // can never end on a dangling chevron.
  const visible = elided.length > 0 ? [...head, "…", ...tail] : folderPath;
  const items = visible.map((text, i) => ({
    text,
    kind:
      elided.length > 0 && i === head.length
        ? ("ellipsis" as const)
        : i === visible.length - 1
          ? ("target" as const)
          : ("segment" as const),
  }));

  return (
    <>
      <DialogTitle className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="min-w-0">
          {t.documents.organize.titleProject}{" "}
          <span className="font-semibold text-red-500">{projectName}</span>
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={t.documents.organize.description}
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{t.documents.organize.description}</TooltipContent>
        </Tooltip>
      </DialogTitle>

      {folderPath.length > 0 && (
        <p
          className="flex min-w-0 items-center gap-1 pl-6 text-sm text-muted-foreground"
          title={fullPath}
          aria-label={`${t.documents.organize.pathLabel}: ${fullPath}`}
        >
          {items.map((item, i) => (
            <React.Fragment key={`${item.kind}-${i}-${item.text}`}>
              {i > 0 && (
                <span className="shrink-0" aria-hidden="true">
                  ›
                </span>
              )}
              <span
                className={cn(
                  "truncate",
                  item.kind === "target" && "font-medium text-foreground",
                  item.kind === "ellipsis" && "shrink-0 cursor-default"
                )}
                title={
                  item.kind === "ellipsis"
                    ? t.documents.organize.pathEllipsisTitle.replace("{names}", elided.join(" › "))
                    : undefined
                }
              >
                {item.text}
              </span>
            </React.Fragment>
          ))}
        </p>
      )}
    </>
  );
}
